import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useListClubsQuery } from '../../redux/clubs/clubs.api';
import { ClubCard } from '../../components/ClubCard';

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

export const AdminClubs = (): React.JSX.Element => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isFetching, error } = useListClubsQuery({
    search: debouncedSearch || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const hasItems = data && data.items.length > 0;
  const empty = data && data.items.length === 0;

  return (
    <div className="admin-users">
      <div className="admin-users__header">
        <div className="admin-users__title">
          <h2>Clubs</h2>
          {data && (
            <span className="admin-users__count">
              {data.total.toLocaleString()} total
            </span>
          )}
        </div>
        <input
          className="admin-users__search"
          type="search"
          placeholder="Search by club name…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>

      {error && (
        <div className="admin-users__error">
          Failed to load clubs. Try again.
        </div>
      )}

      {isLoading && !data && (
        <div className="admin-users__loading">Loading clubs…</div>
      )}

      {empty && (
        <div className="admin-tab-empty">
          <h2 className="admin-tab-empty__title">No clubs</h2>
          <p className="admin-tab-empty__sub">
            {debouncedSearch
              ? `No clubs match "${debouncedSearch}".`
              : 'No clubs yet.'}
          </p>
        </div>
      )}

      {hasItems && (
        <>
          <div className="admin-events__grid">
            {data!.items.map((club) => (
              <ClubCard
                key={club.id}
                club={club}
                onClick={(c) => navigate(`/admin/dashboard/clubs/${c.id}`)}
              />
            ))}
          </div>

          <div className="admin-users__pagination">
            <button
              type="button"
              className="admin-users__page-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isFetching}
            >
              ← Previous
            </button>
            <span className="admin-users__page-info">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="admin-users__page-btn"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || isFetching}
            >
              Next →
            </button>
          </div>
        </>
      )}
    </div>
  );
};
